import React, { useEffect, useState } from 'react';
import ParticipantTile from '@/components/video-call/ParticipantTile';

const gridClassName = (count) => {
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-1 md:grid-cols-2';
  if (count <= 4) return 'grid-cols-1 sm:grid-cols-2';
  if (count <= 9) return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3';
  return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4';
};

const containerClassName = (count) => {
  if (count <= 1) return 'max-w-4xl mx-auto';
  if (count === 2) return 'max-w-5xl mx-auto';
  return '';
};

const VideoGrid = ({ participants }) => {
  const [focusedTileId, setFocusedTileId] = useState(null);

  useEffect(() => {
    if (!focusedTileId) return;
    if (!participants.some((participant) => participant.tileId === focusedTileId)) {
      setFocusedTileId(null);
    }
  }, [focusedTileId, participants]);

  if (!participants.length) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center rounded-[2rem] border border-dashed border-slate-200 bg-slate-50 text-center">
        <div className="max-w-sm px-6">
          <p className="text-base font-semibold text-slate-800">Joining call…</p>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Camera and microphone tracks will appear here as soon as LiveKit finishes connecting.
          </p>
        </div>
      </div>
    );
  }

  const sharedParticipant = participants.find((participant) => participant.tileType === 'screen');
  const featuredParticipant = participants.find((participant) => participant.tileId === focusedTileId) || sharedParticipant;

  const handleTileFocus = (participant) => {
    setFocusedTileId((previousTileId) => {
      if (!sharedParticipant && previousTileId === participant.tileId) {
        return null;
      }
      return participant.tileId;
    });
  };

  if (featuredParticipant && participants.length > 1) {
    const secondaryParticipants = participants.filter((participant) => participant.tileId !== featuredParticipant.tileId);

    return (
      <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_220px] xl:grid-cols-[minmax(0,1fr)_260px]">
        <div className="min-h-0">
          <ParticipantTile
            participant={featuredParticipant}
            variant="featured"
            interactive
            isFocused
            onDoubleClick={() => handleTileFocus(featuredParticipant)}
          />
        </div>

        {secondaryParticipants.length > 0 ? (
          <div className="flex min-h-0 gap-3 overflow-x-auto pb-1 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden">
            {secondaryParticipants.map((participant) => (
              <div key={participant.tileId} className="min-w-[180px] flex-1 lg:min-w-0 lg:flex-none">
                <ParticipantTile
                  participant={participant}
                  variant="compact"
                  interactive
                  isFocused={participant.tileId === focusedTileId}
                  onDoubleClick={() => handleTileFocus(participant)}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`grid h-full content-start auto-rows-min gap-4 ${gridClassName(participants.length)} ${containerClassName(participants.length)}`}>
      {participants.map((participant) => (
        <ParticipantTile
          key={participant.tileId}
          participant={participant}
          interactive={participants.length > 1}
          isFocused={participant.tileId === focusedTileId}
          onDoubleClick={() => handleTileFocus(participant)}
        />
      ))}
    </div>
  );
};

export default VideoGrid;
